import { hex } from "@scure/base";
import {
  ArrowRight,
  Bitcoin,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Coins,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  Upload,
  WalletCards,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import { AccountWalletSelector } from "../../account-control";
import { useAppState } from "../../app-state";
import {
  fetchRuneEtchAvailability,
  fetchRuneEtches,
  fetchUtxos,
  filterCardinalUtxos,
  submitRuneEtch,
} from "../../lib/api";
import { transactionExplorerUrl } from "../../lib/explorer-links";
import { buildRevealTransaction, finalizeCommitPsbt, finalizeRevealPsbt, type InscriptionPlan } from "../../lib/inscriptions";
import { saveRecoveryPackage } from "../../lib/recovery";
import {
  RUNE_COMMIT_CONFIRMATIONS,
  RUNE_ETCH_SERVICE_FEE_SATS,
  RUNE_MAX_DIVISIBILITY,
  RUNE_MAX_LOGO_BYTES,
  createRuneEtchPlan,
  formatRuneAmount,
  normalizeRuneEtchDefinition,
  normalizeSpacedRune,
  runeSupply,
  type RuneEtchAvailability,
  type RuneEtchDefinition,
  type RuneEtchInput,
  type RuneEtchJobSummary,
  type RuneLogoInput,
} from "../../lib/runes";
import type { SignedPackage } from "../../lib/types";
import { keepCardinalUtxos, signPsbtGroups } from "../../lib/wallets";
import { downloadSignedPackage } from "../../lib/workflow";
import "./runes.css";

type LogoMode = RuneLogoInput["kind"];

const initialInput: RuneEtchInput = {
  spacedRune: "",
  symbol: "",
  divisibility: 0,
  premine: "",
  mintable: true,
  amount: "",
  cap: "",
  termsMode: "height",
  start: "",
  end: "",
  turbo: false,
};

const statusCopy: Record<RuneEtchJobSummary["status"], string> = {
  prepared: "Commit prepared",
  maturing: "Commit maturing",
  reveal_broadcast: "Etch broadcast",
  complete: "Rune etched",
  failed: "Needs attention",
};

function sats(value: number): string {
  return `${Math.round(value).toLocaleString()} sats`;
}

function readFile(file: File): Promise<Uint8Array> {
  return file.arrayBuffer().then((value) => new Uint8Array(value));
}

function JobCard({ job }: { job: RuneEtchJobSummary }) {
  return <article className={`rune-job ${job.status}`}>
    <div className="rune-job-mark">{job.definition.symbol || "¤"}</div>
    <div className="rune-job-copy">
      <strong>{job.definition.spacedRune}</strong>
      <span>{statusCopy[job.status]}</span>
      {job.status === "maturing" ? <small>{Math.min(job.confirmations, RUNE_COMMIT_CONFIRMATIONS)} / {RUNE_COMMIT_CONFIRMATIONS} confirmations</small> : null}
      {job.lastError ? <small className="rune-job-error">{job.lastError}</small> : null}
    </div>
    <div className="rune-job-links">
      <a href={transactionExplorerUrl(job.commitTxid)} target="_blank" rel="noreferrer">COMMIT <ExternalLink /></a>
      {(job.status === "reveal_broadcast" || job.status === "complete")
        ? <a href={transactionExplorerUrl(job.revealTxid)} target="_blank" rel="noreferrer">ETCH <ExternalLink /></a>
        : null}
    </div>
  </article>;
}

export function RunesPage() {
  const { authorization, authorizeWallet, config, fees, setWalletModalOpen, wallet } = useAppState();
  const [input, setInput] = useState<RuneEtchInput>(initialInput);
  const [availability, setAvailability] = useState<RuneEtchAvailability | null>(null);
  const [availabilityBusy, setAvailabilityBusy] = useState(false);
  const [logoMode, setLogoMode] = useState<LogoMode>("none");
  const [logoText, setLogoText] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [feeRate, setFeeRate] = useState("2");
  const [plan, setPlan] = useState<InscriptionPlan | null>(null);
  const [definition, setDefinition] = useState<RuneEtchDefinition | null>(null);
  const [signedPackage, setSignedPackage] = useState<SignedPackage | null>(null);
  const [jobs, setJobs] = useState<RuneEtchJobSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (fees?.halfHourFee) setFeeRate(String(fees.halfHourFee));
  }, [fees?.halfHourFee]);

  useEffect(() => {
    const controller = new AbortController();
    const handle = window.setTimeout(() => {
      setAvailabilityBusy(true);
      void fetchRuneEtchAvailability(input.spacedRune, controller.signal)
        .then(setAvailability)
        .catch((caught) => {
          if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : "Rune availability could not be checked");
        })
        .finally(() => { if (!controller.signal.aborted) setAvailabilityBusy(false); });
    }, input.spacedRune.trim() ? 450 : 0);
    return () => {
      controller.abort();
      window.clearTimeout(handle);
    };
  }, [input.spacedRune]);

  useEffect(() => {
    if (!authorization) {
      setJobs([]);
      return;
    }
    let active = true;
    const load = () => fetchRuneEtches(authorization).then((result) => { if (active) setJobs(result); }).catch(() => undefined);
    void load();
    const interval = window.setInterval(() => void load(), 30_000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [authorization]);

  const parsedName = useMemo(() => {
    try { return normalizeSpacedRune(input.spacedRune); }
    catch { return null; }
  }, [input.spacedRune]);

  const supply = useMemo(() => {
    try {
      const normalized = normalizeRuneEtchDefinition(input, availability?.expectedRevealHeight);
      return formatRuneAmount(runeSupply(normalized), normalized.divisibility);
    } catch {
      return "—";
    }
  }, [availability?.expectedRevealHeight, input]);

  const update = <Key extends keyof RuneEtchInput>(key: Key, value: RuneEtchInput[Key]) => {
    setInput((current) => ({ ...current, [key]: value }));
    setPlan(null);
    setDefinition(null);
    setSuccess("");
  };

  const logo = async (): Promise<RuneLogoInput> => {
    if (logoMode === "none") return { kind: "none" };
    if (logoMode === "text") return { kind: "text", text: logoText };
    if (!logoFile) throw new Error("Choose a Rune logo file");
    return { kind: "file", body: await readFile(logoFile), mimeType: logoFile.type || "application/octet-stream", name: logoFile.name };
  };

  const prepare = async (event: FormEvent) => {
    event.preventDefault();
    if (!wallet) {
      setWalletModalOpen(true);
      return;
    }
    if (!config?.marketplaceFeeAddress) {
      setError("Rune creation fee address is not configured");
      return;
    }
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const latest = await fetchRuneEtchAvailability(input.spacedRune);
      setAvailability(latest);
      if (!latest.unlocked) throw new Error(`${input.spacedRune || "This Rune"} is not unlocked at the expected reveal height`);
      if (!latest.available) throw new Error("This Rune name has already been etched or is unavailable");
      const normalized = normalizeRuneEtchDefinition(input, latest.expectedRevealHeight);
      const rate = Number(feeRate);
      if (!Number.isFinite(rate) || rate < 0.1 || rate > 1_000) throw new Error("Miner fee rate must be between 0.1 and 1,000 sat/vB");
      const fetched = await fetchUtxos(wallet.payment.address);
      const cardinal = await filterCardinalUtxos(await keepCardinalUtxos(wallet, fetched));
      const nextPlan = createRuneEtchPlan({
        wallet,
        definition: normalized,
        logo: await logo(),
        utxos: cardinal,
        feeRate: rate,
        marketplaceFeeAddress: config.marketplaceFeeAddress,
      });
      setDefinition(normalized);
      setPlan(nextPlan);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Rune review could not be prepared");
    } finally {
      setBusy(false);
    }
  };

  const signAndCreate = async () => {
    if (!wallet || !plan || !definition) return;
    if (config?.transactionsEnabled === false) {
      setError("Bitcoin transaction submission is disabled in this staging environment");
      return;
    }
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const token = authorization ?? await authorizeWallet();
      if (!token) throw new Error("Wallet verification required");
      const signedCommitPsbt = await signPsbtGroups(wallet, plan.commit.toPSBT(0), {
        [wallet.payment.address]: plan.selectedUtxos.map((_, index) => index),
      });
      const commit = finalizeCommitPsbt(signedCommitPsbt);
      const revealTemplate = buildRevealTransaction(plan, commit.id);
      const reveal = finalizeRevealPsbt(revealTemplate.toPSBT(0), plan);
      const packageValue: SignedPackage = {
        commitTxid: commit.id,
        revealTxid: reveal.id,
        commitHex: commit.hex,
        revealHex: reveal.hex,
        recovery: plan.ephemeralPrivateKey ? {
          ephemeralPrivateKey: hex.encode(plan.ephemeralPrivateKey),
          revealScript: hex.encode(plan.revealPayment.tapLeafScript?.[0]?.[1] ?? new Uint8Array()),
          revealInternalKey: hex.encode(plan.revealPayment.tapInternalKey ?? new Uint8Array()),
          tapMerkleRoot: hex.encode(plan.revealPayment.tapMerkleRoot ?? new Uint8Array()),
        } : undefined,
      };
      await saveRecoveryPackage(wallet.payment.address, packageValue);
      const job = await submitRuneEtch(token, definition, commit.hex, reveal.hex);
      setSignedPackage(packageValue);
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)]);
      setPlan(null);
      setSuccess(`${definition.spacedRune} commit broadcast. The etch will broadcast automatically after six confirmations.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Rune creation could not be submitted");
      if (authorization) void fetchRuneEtches(authorization).then(setJobs).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const resetReview = () => {
    setPlan(null);
    setDefinition(null);
    setError("");
  };

  return <main className="runes-page">
    <section className="runes-hero">
      <div>
        <p className="eyebrow">BITCOIN-NATIVE TOKEN CREATION</p>
        <h1><Coins /> CREATE A RUNE</h1>
        <p>Define supply, premine and public mint terms. Spam The Chain handles the six-block commitment and broadcasts the etch automatically.</p>
      </div>
      {wallet ? <AccountWalletSelector label="ETCH WALLET" /> : <button className="rune-connect" type="button" onClick={() => setWalletModalOpen(true)}><WalletCards /> CONNECT WALLET</button>}
    </section>

    <section className="runes-security-strip">
      <span><ShieldCheck /> SELF-CUSTODY SIGNING</span>
      <span><Bitcoin /> MAINNET RUNES</span>
      <span><strong>{RUNE_ETCH_SERVICE_FEE_SATS.toLocaleString()}</strong> SAT SERVICE FEE</span>
      <span><Clock3 /> {RUNE_COMMIT_CONFIRMATIONS} CONFIRMATION COMMITMENT</span>
    </section>

    <div className="runes-layout">
      <form className="rune-form" onSubmit={(event) => void prepare(event)}>
        <header>
          <div><span>01</span><div><p className="eyebrow">ETCHING</p><h2>Rune details</h2></div></div>
          {availabilityBusy ? <LoaderCircle className="spin" /> : availability?.available && parsedName ? <CheckCircle2 className="ok" /> : null}
        </header>

        <div className="rune-form-grid">
          <label className="rune-field wide">
            <span>RUNE NAME</span>
            <div className="rune-name-input"><input value={input.spacedRune} maxLength={51} spellCheck={false} onChange={(event) => update("spacedRune", event.target.value.toLocaleUpperCase("en-US").replaceAll(".", "•"))} placeholder="WORLD•RESOURCE" /><small>{parsedName?.runeName.length ?? 0} LETTERS</small></div>
            <em>Use A–Z. Add • between letters for display; spacers do not affect uniqueness.</em>
            {parsedName && availability ? <div className={`rune-availability ${availability.available ? "available" : "unavailable"}`}>
              {availability.available ? "AVAILABLE AT EXPECTED REVEAL" : availability.unlocked ? "ALREADY ETCHED" : `LOCKED · MINIMUM ${availability.minimumRune}`}
            </div> : null}
          </label>
          <label className="rune-field">
            <span>SYMBOL <small>OPTIONAL</small></span>
            <input value={input.symbol} maxLength={4} onChange={(event) => update("symbol", Array.from(event.target.value)[0] ?? "")} placeholder="¤" />
            <em>One character shown beside balances.</em>
          </label>
          <label className="rune-field">
            <span>PREMINE <small>OPTIONAL</small></span>
            <input inputMode="decimal" value={input.premine} onChange={(event) => update("premine", event.target.value)} placeholder="0" />
            <em>Allocated to your Ordinals wallet when etched.</em>
          </label>
        </div>

        <section className="rune-mint-box">
          <div className="rune-switch-row">
            <div><strong>PUBLIC MINT</strong><small>Let anyone mint under fixed terms</small></div>
            <button className={input.mintable ? "rune-toggle active" : "rune-toggle"} type="button" role="switch" aria-checked={input.mintable} onClick={() => update("mintable", !input.mintable)}><span /></button>
          </div>
          {input.mintable ? <div className="rune-form-grid">
            <label className="rune-field"><span>AMOUNT PER MINT</span><input inputMode="decimal" value={input.amount} onChange={(event) => update("amount", event.target.value)} placeholder="1000" /></label>
            <label className="rune-field"><span>MINT CAP</span><input inputMode="numeric" value={input.cap} onChange={(event) => update("cap", event.target.value)} placeholder="21000" /></label>
            <div className="rune-supply"><span>MAX SUPPLY</span><strong>{supply}</strong><small>{input.symbol || "¤"}</small></div>
          </div> : <div className="rune-closed-mint"><ShieldCheck /> Fixed supply. Only your premine will exist.</div>}
        </section>

        <section className="rune-logo-box">
          <div><p className="eyebrow">RUNE INSCRIPTION</p><h3>Logo or identity</h3><small>The Rune commitment creates one linked inscription. Leave it blank, inscribe text, or upload a file.</small></div>
          <div className="rune-choice-tabs">
            {(["none", "text", "file"] as LogoMode[]).map((mode) => <button className={logoMode === mode ? "active" : ""} type="button" key={mode} onClick={() => { setLogoMode(mode); setPlan(null); }}>
              {mode === "none" ? <X /> : mode === "text" ? <FileText /> : <ImageIcon />}{mode.toUpperCase()}
            </button>)}
          </div>
          {logoMode === "text" ? <label className="rune-field wide"><span>TEXT TO INSCRIBE</span><textarea rows={5} value={logoText} onChange={(event) => { setLogoText(event.target.value); setPlan(null); }} placeholder="A short description, symbol, or project identity…" /><em>{new TextEncoder().encode(logoText).length.toLocaleString()} / {RUNE_MAX_LOGO_BYTES.toLocaleString()} BYTES</em></label> : null}
          {logoMode === "file" ? <label className="rune-upload"><Upload /><strong>{logoFile ? logoFile.name : "CHOOSE FILE"}</strong><span>{logoFile ? `${logoFile.size.toLocaleString()} bytes` : "Images and other inscription-ready files up to 400 KB"}</span><input type="file" onChange={(event) => { setLogoFile(event.target.files?.[0] ?? null); setPlan(null); }} /></label> : null}
        </section>

        <button className="rune-advanced-toggle" type="button" aria-expanded={advanced} onClick={() => setAdvanced((value) => !value)}><span>ADVANCED TERMS</span><ChevronDown className={advanced ? "open" : ""} /></button>
        {advanced ? <section className="rune-advanced">
          <div className="rune-form-grid">
            <label className="rune-field"><span>DIVISIBILITY</span><input type="number" min="0" max={RUNE_MAX_DIVISIBILITY} value={input.divisibility} onChange={(event) => update("divisibility", Number(event.target.value))} /><em>Decimal places, from 0 to {RUNE_MAX_DIVISIBILITY}.</em></label>
            <div className="rune-field"><span>MINT HEIGHT TYPE</span><div className="rune-segmented"><button type="button" className={input.termsMode !== "offset" ? "active" : ""} onClick={() => update("termsMode", "height")}>ABSOLUTE</button><button type="button" className={input.termsMode === "offset" ? "active" : ""} onClick={() => update("termsMode", "offset")}>RELATIVE</button></div><em>{input.termsMode === "offset" ? "Blocks after the Rune is etched." : "Exact Bitcoin block heights."}</em></div>
            {input.mintable ? <>
              <label className="rune-field"><span>MINT START <small>OPTIONAL</small></span><input inputMode="numeric" value={input.start} onChange={(event) => update("start", event.target.value)} placeholder={input.termsMode === "offset" ? "6" : String((availability?.expectedRevealHeight ?? 0) + 1)} /></label>
              <label className="rune-field"><span>MINT END <small>OPTIONAL</small></span><input inputMode="numeric" value={input.end} onChange={(event) => update("end", event.target.value)} placeholder={input.termsMode === "offset" ? "10000" : String((availability?.expectedRevealHeight ?? 0) + 10000)} /></label>
            </> : null}
          </div>
          <div className="rune-switch-row compact"><div><strong>TURBO MODE</strong><small>Opt in to future compatible protocol upgrades.</small></div><button className={input.turbo ? "rune-toggle active" : "rune-toggle"} type="button" role="switch" aria-checked={input.turbo} onClick={() => update("turbo", !input.turbo)}><span /></button></div>
        </section> : null}

        <section className="rune-fee-row">
          <label className="rune-field"><span>MINER FEE RATE</span><div className="rune-suffix"><input type="number" min="0.1" max="1000" step="0.1" value={feeRate} onChange={(event) => { setFeeRate(event.target.value); setPlan(null); }} /><small>SAT/VB</small></div></label>
          <div><span>SERVICE FEE</span><strong>{sats(RUNE_ETCH_SERVICE_FEE_SATS)}</strong><small>One fixed fee, enforced on the server.</small></div>
        </section>

        {error && !plan ? <div className="rune-alert error" role="alert"><ShieldCheck /> {error}</div> : null}
        {success ? <div className="rune-alert success" role="status"><CheckCircle2 /> <span>{success}{signedPackage ? <button type="button" onClick={() => downloadSignedPackage(signedPackage)}>DOWNLOAD RECOVERY PACKAGE</button> : null}</span></div> : null}
        <button className="rune-primary" type="submit" disabled={busy || availabilityBusy}>{busy ? <LoaderCircle className="spin" /> : <ShieldCheck />} REVIEW RUNE ETCH <ArrowRight /></button>
      </form>

      <aside className="rune-side">
        <section className="rune-live-card">
          <div><span>BITCOIN HEIGHT</span><strong>{availability?.tipHeight.toLocaleString() ?? "—"}</strong></div>
          <div><span>EXPECTED REVEAL</span><strong>{availability?.expectedRevealHeight.toLocaleString() ?? "—"}</strong></div>
          <div><span>MINIMUM NAME</span><strong>{availability?.minimumRune ?? "—"}</strong></div>
          <button type="button" disabled={availabilityBusy} onClick={() => { setAvailabilityBusy(true); void fetchRuneEtchAvailability(input.spacedRune).then(setAvailability).catch((caught) => setError(caught instanceof Error ? caught.message : "Status could not be refreshed")).finally(() => setAvailabilityBusy(false)); }}><RefreshCw className={availabilityBusy ? "spin" : ""} /> REFRESH NETWORK</button>
        </section>
        <section className="rune-process-card">
          <p className="eyebrow">HOW IT WORKS</p>
          <ol>
            <li><span>1</span><div><strong>Review and sign</strong><small>Your payment wallet funds the network cost and 2,000-sat fee.</small></div></li>
            <li><span>2</span><div><strong>Commit confirms</strong><small>The hidden name commitment matures for six Bitcoin confirmations.</small></div></li>
            <li><span>3</span><div><strong>Etch broadcasts</strong><small>The pre-signed reveal is sent automatically. No second visit is required.</small></div></li>
          </ol>
        </section>
        {jobs.length ? <section className="rune-jobs-card"><header><div><p className="eyebrow">MY RUNES</p><h3>Etch activity</h3></div><span>{jobs.length}</span></header><div>{jobs.map((job) => <JobCard job={job} key={job.id} />)}</div></section> : null}
      </aside>
    </div>

    {plan && definition ? <div className="rune-review-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) resetReview(); }}>
      <section className="rune-review" role="dialog" aria-modal="true" aria-labelledby="rune-review-title">
        <header><div><p className="eyebrow">FINAL REVIEW</p><h2 id="rune-review-title">{definition.spacedRune}</h2></div><button type="button" aria-label="Close Rune review" disabled={busy} onClick={resetReview}><X /></button></header>
        <div className="rune-review-symbol">{definition.symbol}</div>
        <dl>
          <div><dt>Maximum supply</dt><dd>{formatRuneAmount(runeSupply(definition), definition.divisibility)} {definition.symbol}</dd></div>
          <div><dt>Premine</dt><dd>{formatRuneAmount(BigInt(definition.premine), definition.divisibility)} {definition.symbol}</dd></div>
          <div><dt>Public mint</dt><dd>{definition.terms ? `${formatRuneAmount(BigInt(definition.terms.amount), definition.divisibility)} × ${BigInt(definition.terms.cap).toLocaleString()}` : "Closed"}</dd></div>
          <div><dt>Network fees</dt><dd>{sats(plan.summary.networkFee)}</dd></div>
          <div><dt>Spam The Chain fee</dt><dd>{sats(plan.summary.marketplaceFee)}</dd></div>
          <div className="total"><dt>Total wallet spend</dt><dd>{sats(plan.summary.total)}</dd></div>
        </dl>
        <div className="rune-review-warning"><Clock3 /><p><strong>The commit cannot be reversed after broadcast.</strong><span>Your signed etch waits for six confirmations, then broadcasts automatically. Keep the recovery package until the Rune is confirmed.</span></p></div>
        {config?.transactionsEnabled === false ? <div className="rune-alert error"><ShieldCheck /> Signing and Bitcoin broadcasts are disabled on staging.</div> : null}
        {error ? <div className="rune-alert error" role="alert"><ShieldCheck /> {error}</div> : null}
        <button className="rune-primary" type="button" disabled={busy || config?.transactionsEnabled === false} onClick={() => void signAndCreate()}>{busy ? <LoaderCircle className="spin" /> : <WalletCards />} SIGN & CREATE RUNE</button>
        <button className="rune-cancel" type="button" disabled={busy} onClick={resetReview}>BACK TO EDIT</button>
      </section>
    </div> : null}
  </main>;
}
